import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  IconButton,
  Typography,
  Paper,
  Grid,
  Chip,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import api from '../services/api';

const ImageUpload = ({ 
  images = [], 
  onImagesChange, 
  maxImages = 5, 
  title = "Images du produit",
  onDeleteImage = null, // Callback pour la suppression d'image
  onSetPrimaryImage = null, // Callback pour définir l'image primaire
  entityType = 'product', // Type d'entité: 'product', 'variant', 'category', 'subcategory'
  entityId = null // ID de l'entité
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [deletingImage, setDeletingImage] = useState(null);
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFiles = (files) => {
    const newImages = Array.from(files).map(file => ({
      id: Date.now() + Math.random(),
      file,
      preview: URL.createObjectURL(file),
      name: file.name,
    }));

    // Compter seulement les nouvelles images (pas les existantes)
    const existingImages = images.filter(img => !img.isExisting);
    
    if (existingImages.length + newImages.length <= maxImages) {
      onImagesChange([...images, ...newImages]);
    } else {
      alert(`Vous ne pouvez pas ajouter plus de ${maxImages} images`);
    }
  };

  // Fonction pour obtenir le nom d'affichage de l'image
  const getImageName = (image) => {
    if (image.name) return image.name;
    if (image.alt) return image.alt;
    if (image.file && image.file.name) return image.file.name;
    return 'Image sans nom';
  };

  // Fonction pour obtenir la source de l'image
  const getImageSource = (image) => {
    if (image.preview) return image.preview;
    if (image.url) return image.url;
    return '';
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const removeImage = async (imageId) => {
    const imageToRemove = images.find(img => img.id === imageId);
    console.log('imageToRemove:', imageToRemove);
    if (!imageToRemove) return;

    try {
      setDeletingImage(imageId);
      
      // Si c'est une image existante, la supprimer du serveur
      if (imageToRemove.isExisting) {
        await deleteImageFromServer(imageToRemove.id);
      }
      
      // Supprimer l'image de la liste locale
      const updatedImages = images.filter(img => img.id !== imageId);
      onImagesChange(updatedImages);

    } catch (error) {
      console.error('Erreur lors de la suppression de l\'image:', error);
      // Même en cas d'erreur, supprimer l'image de la liste locale
      const updatedImages = images.filter(img => img.id !== imageId);
      onImagesChange(updatedImages);
    } finally {
      setDeletingImage(null);
    }
  };

  // Fonction pour supprimer une image directement via l'API
  const deleteImageFromServer = async (imageId) => {
    const baseUrl = 'https://api.inklandtattoo.fr';
    let deleteUrl = '';
    
    switch (entityType) {
      case 'product':
        deleteUrl = `/api/product-images/${imageId}`;
        break;
      case 'variant':
        deleteUrl = `/api/variant-images/${imageId}`;
        break;
      case 'category':
        deleteUrl = `/api/category-images/${imageId}`;
        break;
      case 'subcategory':
        deleteUrl = `/api/subcategory-images/${imageId}`;
        break;
      default:
        throw new Error(`Type d'entité non supporté: ${entityType}`);
    }
    
    const response = await api.delete(deleteUrl);
    return response.data;
  };

  // Fonction pour vérifier si une image peut être supprimée
  const canRemoveImage = (image) => {
    // Les images existantes peuvent toujours être supprimées
    if (image.isExisting) return true;
    // Les nouvelles images peuvent être supprimées
    if (image.file) return true;
    // Les images temporaires peuvent être supprimées
    if (image.isTemp) return true;
    return false;
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  return (
    <Box>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {/* Zone de drop */}
              <Paper
          sx={{
            border: '2px dashed',
            borderColor: dragActive ? 'primary.main' : 'grey.600',
            borderRadius: 2,
            p: 3,
            textAlign: 'center',
            backgroundColor: dragActive ? 'rgba(144, 202, 249, 0.1)' : 'background.paper',
            cursor: 'pointer',
            mb: 2,
          }}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={openFileDialog}
      >
        <CloudUploadIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
        <Typography variant="h6" color="textSecondary" gutterBottom>
          Glissez-déposez vos images ici
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          ou cliquez pour sélectionner des fichiers
        </Typography>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          sx={{ mt: 2 }}
          onClick={(e) => {
            e.stopPropagation();
            openFileDialog();
          }}
        >
          Ajouter des images
        </Button>
        <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
          Formats acceptés: JPG, PNG, GIF • Maximum {maxImages} images
        </Typography>
      </Paper>

      {/* Affichage des images existantes */}
      {images.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Images actuelles ({images.length})
          </Typography>
          <Grid container spacing={2}>
            {images.map((image) => (
              <Grid item xs={12} sm={6} md={4} key={image.id}>
                <Paper
                  sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: 2,
                    '&:hover .image-actions': {
                      opacity: 1,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      height: 200,
                      backgroundImage: `url(${getImageSource(image)})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      position: 'relative',
                    }}
                  >
                    {/* Overlay avec actions */}
                    <Box
                      className="image-actions"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0,
                        transition: 'opacity 0.3s ease',
                      }}
                    >
                      <IconButton
                        color="error"
                        onClick={() => removeImage(image.id)}
                        disabled={deletingImage === image.id}
                        sx={{ backgroundColor: 'rgba(255, 255, 255, 0.9)' }}
                      >
                        {deletingImage === image.id ? (
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              border: '2px solid #f44336',
                              borderTop: '2px solid transparent',
                              borderRadius: '50%',
                              animation: 'spin 1s linear infinite',
                            }}
                          />
                        ) : (
                          <DeleteIcon />
                        )}
                      </IconButton>
                    </Box>
                    
                    {/* Badge pour les images existantes */}
                    {image.isExisting && (
                      <Chip
                        label="Existant"
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 8,
                          left: 8,
                          backgroundColor: 'success.main',
                          color: 'white',
                        }}
                      />
                    )}
                  </Box>
                  
                  {/* Nom de l'image */}
                  <Box sx={{ p: 1 }}>
                    <Typography variant="caption" noWrap>
                      {getImageName(image)}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

    </Box>
  );
};

export default ImageUpload;
